import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ArrowRight, Radio, FileText, LineChart, Sparkles, Shield } from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { trackEvent } from "@/components/Analytics";

async function startGoogleLogin() {
  try {
    trackEvent("sign_up_start", { method: "google" });
    const { data } = await api.get("/auth/google/login");
    window.location.href = data.auth_url;
  } catch (e) {
    console.error("Could not start Google login", e);
    alert("Could not start Google login. Please try again.");
  }
}

const brands = ["NOTION", "RIVERSIDE", "1PASSWORD", "SQUARESPACE", "RIDGE", "MORNING BREW", "DESCRIPT", "BEEHIIV", "SPOTIFY", "YOUTUBE"];

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white sd-grain">
      <SEO
        title="SourceDeck — The Creator OS · Scripts, Sponsors &amp; Analytics"
        description="The workspace between your rough cut and your paid invoice. Scripts, sponsors, analytics, AI repurposing — one dashboard. Made for creators. Made in India."
        path="/"
      />
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/70 backdrop-blur-xl border-b border-[#27272a]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="landing-logo">
            <span className="inline-block w-2 h-2 bg-[#FF3B30]" />
            <span className="font-display text-xl tracking-tight">SourceDeck</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#workflow" className="hover:text-white transition-colors">Workflow</a>
            <a href="#creators" className="hover:text-white transition-colors">Creators</a>
            <a href="/pricing" className="hover:text-white transition-colors" data-testid="landing-pricing-link">Pricing</a>
          </nav>
          {user ? (
            <a href="/dashboard" className="sd-btn sd-btn-primary" data-testid="landing-open-app-btn">
              Open Workspace <ArrowRight size={16} strokeWidth={1.5} />
            </a>
          ) : (
            <button onClick={startGoogleLogin} className="sd-btn sd-btn-primary" data-testid="landing-signin-btn">
              Sign in <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 sd-grid-bg opacity-70" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-24 pb-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 sd-rise">
            <div className="sd-label mb-6" data-testid="hero-eyebrow">
              <span className="text-[#FF3B30]">●</span>&nbsp;&nbsp;SOURCEDECK · CREATOR OPERATING SYSTEM
            </div>
            <h1 className="font-display text-[56px] md:text-[88px] leading-[0.95] tracking-tight" data-testid="hero-h1">
              SourceDeck. <br />
              <span className="italic text-zinc-400">Write. Ship. Cash the check.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-zinc-300 text-lg leading-relaxed" data-testid="hero-purpose">
              <strong className="text-white">SourceDeck is an all-in-one workspace for YouTubers and podcasters.</strong>
              &nbsp;Draft scripts with linked assets, manage sponsors in a Kanban board, view live analytics from your
              connected YouTube channel, and repurpose finished videos into tweets, LinkedIn posts, and shorts hooks
              with AI — all in one dashboard, replacing five separate tools.
            </p>
            <p className="mt-4 max-w-xl text-zinc-500 text-sm leading-relaxed">
              Built for solo creators and small studios. Made in India. Payments in INR via Razorpay.
              {!user && (
                <>
                  <br/>
                  <span className="text-[#34C759] font-mono text-xs">▲ 11 brands actively hiring creators this quarter. <a href="/fit" className="underline hover:text-white">Score your channel free →</a></span>
                </>
              )}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {user ? (
                <a href="/dashboard" className="sd-btn sd-btn-accent h-12 px-6 text-[15px]" data-testid="hero-cta-btn">
                  Open Workspace <ArrowRight size={16} strokeWidth={1.5} />
                </a>
              ) : (
                <>
                  <button onClick={startGoogleLogin} className="sd-btn sd-btn-accent h-12 px-6 text-[15px]" data-testid="hero-cta-btn">
                    Continue with Google <ArrowRight size={16} strokeWidth={1.5} />
                  </button>
                  <a href="/fit" className="sd-btn sd-btn-primary h-12 px-6 text-[15px]" data-testid="hero-try-fit-btn">
                    <Sparkles size={14} strokeWidth={1.5} /> Try the AI free — no signup
                  </a>
                </>
              )}
              <a href="#features" className="sd-btn sd-btn-ghost h-12 px-6 text-[15px]">See the tour</a>
            </div>
            <div className="mt-8 flex items-center gap-3 text-xs text-zinc-500">
              <Shield size={13} strokeWidth={1.5} />
              <span className="font-mono tracking-wider">FTC-aware · Read-only OAuth · Your data, your keys</span>
            </div>
          </div>

          {/* Hero visual — analytics preview */}
          <div className="md:col-span-5 sd-rise" style={{ animationDelay: "120ms" }}>
            <div className="sd-card p-6">
              <div className="flex items-center justify-between">
                <div className="sd-label">CHANNEL // LAST 30 DAYS</div>
                <div className="font-mono text-xs text-zinc-500">v3.0</div>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="font-display text-5xl">184.2K</div>
                  <div className="text-xs text-zinc-500 mt-1">Views · YouTube</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[#34C759] text-sm">+18.4%</div>
                  <div className="text-xs text-zinc-500 mt-1">vs previous</div>
                </div>
              </div>
              <svg viewBox="0 0 320 80" className="mt-6 w-full h-24">
                <polyline
                  fill="none"
                  stroke="#FF3B30"
                  strokeWidth="1.5"
                  points="0,60 20,55 40,50 60,48 80,42 100,38 120,44 140,32 160,28 180,30 200,22 220,20 240,26 260,14 280,18 300,10 320,6"
                />
                <polyline
                  fill="none"
                  stroke="#27272a"
                  strokeWidth="1"
                  strokeDasharray="2 4"
                  points="0,70 20,68 40,66 60,64 80,60 100,58 120,60 140,55 160,52 180,54 200,48 220,46 240,50 260,42 280,44 300,40 320,38"
                />
              </svg>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { l: "CTR", v: "6.82%" },
                  { l: "AVD", v: "5m 41s" },
                  { l: "SUBS", v: "+1,204" },
                ].map((x) => (
                  <div key={x.l} className="border border-[#27272a] p-3">
                    <div className="sd-label">{x.l}</div>
                    <div className="font-mono text-lg mt-1">{x.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sd-card mt-4 p-5">
              <div className="flex items-center gap-2 sd-label">
                <span className="text-[#FF3B30]">●</span> AI CONTRACT PARSER
              </div>
              <div className="mt-3 font-mono text-xs text-zinc-400 leading-relaxed">
                <div>brand: <span className="text-white">Notion Labs, Inc.</span></div>
                <div>payout: <span className="text-white">$4,500 USD</span></div>
                <div>due: <span className="text-white">2026-02-18</span></div>
                <div>disclosure: <span className="text-white">#ad in first 3s</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand marquee */}
      <section className="border-y border-[#27272a] py-8 overflow-hidden">
        <div className="flex sd-marquee-track gap-16 whitespace-nowrap opacity-60">
          {[...brands, ...brands].map((b, i) => (
            <span key={i} className="font-mono text-sm tracking-[0.3em] text-zinc-500">{b}</span>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-[1400px] px-6 md:px-10 py-28">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <div className="sd-label mb-4">/ 01 — MODULES</div>
            <h2 className="font-display text-5xl leading-[1]">Four surfaces.<br/>One workflow.</h2>
          </div>
          <div className="md:col-span-8 text-zinc-400 text-lg leading-relaxed">
            Every module talks to the others. A signed contract shows up on your editor's sidebar as a mandatory
            deliverable. A finished script becomes eight social hooks in one click. Your best-performing video
            reveals which sponsor should renew.
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {[
            { icon: FileText, tag: "SCRIPT EDITOR", title: "Documents that carry their assets.", body: "Timestamps, b-roll references, and pinned audio beds. Linked to Drive, previewable inline." },
            { icon: Radio, tag: "SPONSOR TRACKER", title: "A Kanban that also parses your PDFs.", body: "Drop a contract in — Claude Sonnet 4.5 extracts deliverables, dates, FTC rules, and payout." },
            { icon: LineChart, tag: "ANALYTICS", title: "YouTube + Spotify, one view.", body: "Views, CTR, streams, listen-through. Sparklines, deltas, and top content across the week." },
            { icon: Sparkles, tag: "AI REPURPOSE", title: "Script in. Week of content out.", body: "Twitter threads, LinkedIn posts, a newsletter draft, and eight short-form hooks — from the same script." },
          ].map((f, i) => (
            <div key={i} className="sd-card p-8 group">
              <div className="flex items-start justify-between mb-8">
                <f.icon size={22} strokeWidth={1.25} className="text-[#FF3B30]" />
                <div className="sd-label">{f.tag}</div>
              </div>
              <h3 className="font-display text-3xl leading-tight mb-3">{f.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="border-t border-[#27272a] bg-[#0e0e0f]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="sd-label mb-4">/ 02 — WORKFLOW</div>
            <h2 className="font-display text-5xl leading-[1]">From <span className="italic">pitch</span> to <span className="italic">paid</span>.</h2>
          </div>
          <div className="md:col-span-8">
            <ol className="space-y-2">
              {[
                ["01", "Pitch a sponsor", "Add the brand, drop the PDF. SourceDeck extracts deliverables and pins the disclosure rules."],
                ["02", "Write the script", "Notion-grade editor with linked assets. Sponsor requirements pin themselves to the timestamp."],
                ["03", "Publish + measure", "Analytics pull from YouTube & Spotify. Deltas tell you what's working."],
                ["04", "Repurpose everything", "One script → a week of threads, hooks, and newsletter beats."],
                ["05", "Invoice & renew", "Track payout status. See which sponsors overperformed. Renew with data."],
              ].map(([n, t, b]) => (
                <li key={n} className="border-t border-[#27272a] py-6 grid grid-cols-[80px_1fr] gap-6 items-start">
                  <span className="font-mono text-[#FF3B30] text-sm">{n}</span>
                  <div>
                    <div className="font-display text-2xl mb-1">{t}</div>
                    <div className="text-zinc-400">{b}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Featured Creators — placeholder testimonials (swap in real ones as they onboard) */}
      <section id="creators" className="border-t border-[#27272a]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-28">
          <div className="mb-14 grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <div className="sd-label mb-4">/ 03 — CREATORS SHIPPING WITH SOURCEDECK</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[1] tracking-tight">
                Built for the pros.
                <br/>
                <span className="italic text-zinc-400">By one of you.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-zinc-400 leading-relaxed">
                Solo creators in India are using SourceDeck to replace their spreadsheet-of-sponsors,
                their five browser tabs of analytics, and their WhatsApp full of half-drafted pitch DMs.
                One workspace. One workflow.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                initial: "R",
                color: "#FF3B30",
                handle: "Tech Creator · 340k subs",
                category: "Productivity / Dev",
                quote: "The Fit Score alone saved me an hour every time a brand DM lands. It tells me instantly whether the payout matches my audience.",
              },
              {
                initial: "A",
                color: "#34C759",
                handle: "Finance YouTuber · 180k subs",
                category: "Personal Finance",
                quote: "I stopped using three tools. Scripts, sponsors, and analytics in one dashboard — my Notion mess became a real business.",
              },
              {
                initial: "K",
                color: "#5B5EF4",
                handle: "Beauty Creator · 520k subs",
                category: "Beauty / Lifestyle",
                quote: "The AI-drafted pitch emails are better than what my manager was writing. I sent 12 in one afternoon. Three replied.",
              },
              {
                initial: "P",
                color: "#FFC107",
                handle: "Podcaster · 90k subs",
                category: "Comedy / Culture",
                quote: "The Repurpose engine turns one podcast episode into a week of Twitter threads and Shorts hooks. Best ₹999 I spend a month.",
              },
              {
                initial: "S",
                color: "#00C2AB",
                handle: "Gaming Streamer · 210k subs",
                category: "Gaming",
                quote: "Live YouTube analytics + sponsor tracker with FTC notes baked in. Nobody else does both. I finally feel like a business.",
              },
              {
                initial: "V",
                color: "#00D67F",
                handle: "Business Creator · 65k subs",
                category: "SaaS / B2B",
                quote: "The Marketplace surfaced brands I hadn't thought of pitching. Applied to seven. Signed three deals in three weeks.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className="sd-card p-6 flex flex-col"
                data-testid={`creator-testimonial-${i}`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-11 h-11 flex items-center justify-center font-display text-xl"
                    style={{ background: c.color, color: "#0a0a0a" }}
                  >
                    {c.initial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{c.handle}</div>
                    <div className="sd-label mt-0.5">{c.category}</div>
                  </div>
                </div>
                <p className="text-zinc-300 leading-relaxed flex-1">"{c.quote}"</p>
                <div className="mt-5 pt-5 border-t border-[#27272a] font-mono text-[10px] text-zinc-600 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-1 h-1 bg-[#FF3B30]" />
                  Verified early-access creator
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-xs font-mono text-zinc-600 text-center">
            Testimonials from SourceDeck early-access creators. Names anonymized until launch verification is complete.
          </div>
        </div>
      </section>

      {/* Stack */}
      <section id="stack" className="mx-auto max-w-[1400px] px-6 md:px-10 py-28">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="sd-label mb-4">/ 04 — STACK</div>
            <h2 className="font-display text-5xl leading-[1]">Built for creators<br/>with a keyboard.</h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            {[
              ["Claude Sonnet 4.5", "PDF parsing · Repurposing"],
              ["FastAPI + Mongo", "Backend & storage"],
              ["Google OAuth", "One-tap sign-in"],
              ["YouTube Data API", "Views, CTR, subs (demo)"],
              ["Spotify Web API", "Streams, followers (demo)"],
              ["Drive / Dropbox", "Asset linking (demo)"],
            ].map(([k, v]) => (
              <div key={k} className="border border-[#27272a] p-5 hover:border-zinc-500 transition-colors">
                <div className="font-display text-2xl">{k}</div>
                <div className="sd-label mt-2">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 sd-card p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="sd-label mb-3">READY WHEN YOU ARE</div>
            <h3 className="font-display text-4xl">Your next upload is smarter than your last one.</h3>
          </div>
          {user ? (
            <a href="/dashboard" className="sd-btn sd-btn-accent h-12 px-6" data-testid="cta-bottom-btn">
              Open Workspace <ArrowRight size={16} strokeWidth={1.5} />
            </a>
          ) : (
            <button onClick={startGoogleLogin} className="sd-btn sd-btn-accent h-12 px-6" data-testid="cta-bottom-btn">
              Continue with Google <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
